/**
 * EXPERIMENT COPY — do not confuse with the production file.
 *
 * Copied verbatim from `features/billing/dialogs/SubscriptionWizardDialog.tsx` and free to diverge. The original still
 * serves every user; this renders only when `isBillingExperimentOn()` is true
 * (see is-billing-experiment-on.ts). Editing the original changes production.
 *
 * Wizard shell: step routing, titles, footer. Mounts the experiment steps + hook.
 *
 * Shared leaves (plan helpers, Stripe card form, plan table, screen selector,
 * billing store, GraphQL ops) are still imported from the production feature, so
 * this copy stays small. Copy one in here only when the experiment needs to
 * change it — see experiment/README.md.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { Loader2, Home, Briefcase, CreditCard, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, cn } from '@scio3/ui';
import { GET_APP_CFG } from '@scio3/graphql-client';
import { BILLING_INTERVAL, PLAN_FEATURES, PLAN_TEXT_FEATURES } from '@scio3/shared-types';
import { useSubscriptionWizard, STEP } from './useSubscriptionWizard';
import type { WizardStep } from './useSubscriptionWizard';
import { useWizardUrlHash } from './useWizardUrlHash';
import { useTeamStore } from '@/stores/team-store';
import { ProcessingOverlay } from './ProcessingOverlay';
import { ScreenManagementDialog } from '../dialogs/ScreenManagementDialog';
import { ChangePlanWarningDialog } from './wizard/ChangePlanWarningDialog';
import { LeaveCheckoutDialog } from './wizard/LeaveCheckoutDialog';
import { TeamPlanBlockDialog } from './wizard/TeamPlanBlockDialog';
import { CancelSubscriptionDialog } from '../dialogs/CancelSubscriptionDialog';
import { ONE_COLUMN_TRACK, WizardStepScreens } from './wizard/WizardStepScreens';
import { WizardStepConfirmation } from './wizard/WizardStepConfirmation';
import { submitStripeForm } from './stripe-form-submit';
import { WizardStepPlan } from './wizard/WizardStepPlan';
import { CurrencySelect } from './wizard/CurrencySelect';
import { isSameAlias } from './wizard/usePlanCatalog';
import { getPlanDisplayName } from '../utils/plan-helpers';
import { buildTrialSubtitle } from './wizard/trial-subtitle';
import { WizardStepFinish } from './wizard/WizardStepFinish';
import { SubscribedThankYou } from './wizard/SubscribedThankYou';
import type { SubscribedChargeResult } from './wizard/SubscribedThankYou';

// ---------------------------------------------------------------------------
// Step indicator config
// ---------------------------------------------------------------------------

/** Step indicators are only rendered for trial users, who follow the
 *  PLAN → NUMBER_SCREEN → CONFIRMATION flow (plan-first). */
const STEP_INDICATORS = [
  { step: STEP.PLAN, icon: Briefcase, label: 'Select Plan' },
  { step: STEP.NUMBER_SCREEN, icon: Home, label: 'Screen Number' },
  { step: STEP.CONFIRMATION, icon: CreditCard, label: 'Payment' },
] as const;

/** Map wizard step to step indicator index (0-2) */
function getIndicatorIndex(step: WizardStep): number {
  switch (step) {
    case STEP.PLAN:
      return 0;
    case STEP.NUMBER_SCREEN:
      return 1;
    case STEP.CONFIRMATION:
      return 2;
    case STEP.FINISH:
      return 3;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubscriptionWizardDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const w = useSubscriptionWizard();
  const activeIndicatorIndex = getIndicatorIndex(w.step);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showLeaveCheckout, setShowLeaveCheckout] = useState(false);

  /**
   * The corner X on the Payment step asks before closing (LeaveCheckoutDialog): it is the last
   * step, so an accidental X throws away a checkout that is one click from done. Earlier steps
   * close straight away, and the header back arrow is untouched (it steps back, it does not
   * leave). Skipped while a submit is in flight, where handleOpenChange ignores the close anyway.
   */
  const handleWizardOpenChange = (open: boolean) => {
    if (!open && w.step === STEP.CONFIRMATION && !w.isSubmitting) {
      setShowLeaveCheckout(true);
      return;
    }
    w.handleOpenChange(open);
  };

  /**
   * The plan step opens on MONTHLY; the Configure step opens on YEARLY.
   *
   * Two different jobs. The plan cards are a tier comparison, and the monthly rate is the number
   * people carry in their heads, so quoting the discounted annual equivalent there prices every
   * tier below what a month actually costs. Configure is where the term is committed, and yearly
   * is the better default at the point of purchase — which is what `initialInterval` in
   * useSubscriptionWizard already does for anyone never billed.
   *
   * So the wizard's own `billingInterval` keeps its Yearly default (Configure reads it directly)
   * and the plan step DISPLAYS monthly until the toggle is actually touched. Once it is, that
   * choice is the user's and it carries forward — a term picked here is the term on the next
   * step, which is the whole reason the toggle writes to the real interval rather than a copy.
   */
  const [planTermTouched, setPlanTermTouched] = useState(false);

  // Gives the full-page wizard a URL (`#pricing`) so it can be linked to and so the browser
  // Back button closes it instead of leaving the app. See useWizardUrlHash.
  useWizardUrlHash(w.isOpen, () => w.handleOpenChange(false));

  // Load AppCfg to check if TypeForm cancel subscription is configured
  const { data: appCfgData } = useQuery<{ getAppCfgs: Record<string, unknown> }>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId || !w.isOpen,
    fetchPolicy: 'cache-and-network',
  });
  const typeFormCancelEmbed = appCfgData?.getAppCfgs?.typeFormCancelSubscriptionEmbed as
    | string
    | undefined;

  // Show "Cancel Subscription" button — scio1 conditions:
  // 1. TypeForm embed configured, 2. has screens, 3. not trial, 4. not free plan,
  // 5. not team billing (team-billing cancel = reduce screens to 0, no survey).
  //
  // QC #1310 — sub-account DOES show the Cancel button in scio1 (mirrors parent
  // account behavior — TypeForm survey + Cancel mutation accepts subAccountId).
  // Previous gate `!w.contextSubAccountId` was wrong; removed.
  const showCancelButton =
    !w.contextTeamId &&
    !!typeFormCancelEmbed &&
    (w.maxScreens ?? 0) > 0 &&
    !w.isTrial &&
    w.currentPlanFeature !== PLAN_FEATURES.FREE_PLAN;

  // #1113 — when a paid user reduces screens to 0 and clicks Update, route
  // through the TypeForm cancel-survey flow instead of the regular paid-update
  // path. Same effect as clicking Cancel Subscription. Matches scio1
  // (subscriptionPlanModal.js:2054).
  const shouldRouteToCancelOnUpdate = showCancelButton && w.screenCount === 0;

  /**
   * The full-screen page chrome: back arrow + title + currency in a header that scrolls with the
   * content, page-style margins, and no stepper strip. Plan, Configure and Payment all wear it.
   * FINISH keeps the original dialog header.
   */
  const usesRedesignedChrome =
    w.step === STEP.PLAN || w.step === STEP.NUMBER_SCREEN || w.step === STEP.CONFIRMATION;
  /**
   * All three suppress the shared footer: their forward action lives in the content (a plan
   * card's CTA, the summary's button, the Payment step's own submit) and Back is the header's
   * arrow, so a footer would be a second Next and a second Previous.
   *
   * Payment is the one that needs saying. Production's `WizardStepConfirmation` puts its submit
   * button in an Account Summary sidebar it only renders when `isShowConfirm`
   * (`!isTrial || !!existingCard`) holds, and leans on this footer for the other case — a trial
   * user with no card on file, who gets the summary-less "Payment method" layout. That is what
   * the step's `inPageSubmit` supplies instead, so the footer has nothing left to do here.
   */
  /**
   * The plan the Configure step is configuring, by name, so the title can say
   * "Configure your Pro Plus plan" instead of leaving the plan unnamed.
   *
   * `aliasData.name` is the catalog's own label for the alias being charged — the same string the
   * plan card carried — so the title cannot disagree with the card that was just clicked. It is
   * refetched per plan change (network-only), and while that is in flight we resolve the name
   * locally from `selectedPlan` rather than reach for `w.planName`, whose own fallback reads
   * `currentPlanFeature`: on an upgrade that would flash the OLD plan's name in the heading.
   *
   * The local lookup goes through `isSameAlias` because the two spellings differ — the catalog
   * says `proPlus`, `company.payment.planFeature` says `proplus`, and `selectedPlan` seeds from
   * the latter, so an exact-key lookup would print the raw slug "proplus" as a page heading.
   *
   * No resolvable name (an alias outside the map, or the catalog not yet loaded on a fresh
   * `skipPlanStep` entry) falls back to the unnamed title. A heading is the wrong place to guess.
   */
  const configuredPlanName = (() => {
    const catalogName = w.aliasData?.name;
    if (catalogName) return catalogName;
    const alias = Object.keys(PLAN_TEXT_FEATURES).find((key) => isSameAlias(key, w.selectedPlan));
    return alias ? PLAN_TEXT_FEATURES[alias] : undefined;
  })();
  const redesignedTitle =
    w.step === STEP.PLAN
      ? (w.contextTitle ??
        (w.contextMinPlan ? t('Upgrade Required') : t('Choose the best plan for you')))
      : w.step === STEP.CONFIRMATION
        ? t('Payment')
        : configuredPlanName
          ? t('Configure your {{plan}} plan', { plan: configuredPlanName })
          : t('Configure your plan');
  /**
   * One line of trial context under the plan-step title. The rule — including why a Free-plan
   * account must be excluded even though `isTrial` is true for it (#2157) — lives in
   * `buildTrialSubtitle`, where it can be tested without mounting the wizard.
   */
  const trialSubtitle = buildTrialSubtitle({
    isTrial: w.isTrial,
    isOnFreePlan: w.isOnFreePlan,
    currentPlanFeature: w.currentPlanFeature,
    trialEndDate: w.trialEndDate,
    getPlanName: getPlanDisplayName,
    t,
  });

  // Back goes to the previous step, or closes the wizard from the first one. `skipPlanStep`
  // callers (a team/sub-account already in scope) never had a plan step to go back to.
  const canGoBackAStep =
    (w.step === STEP.NUMBER_SCREEN && !w.skipPlanStep) || w.step === STEP.CONFIRMATION;
  /**
   * The back control names where it goes, on every step that has one.
   *
   * It used to be a bare arrow everywhere but one case (a subscriber on Configure), on the
   * theory that a single step back needs no name. On a full-screen wizard it does. There is no
   * dialog frame for the arrow to sit "inside", so an unlabelled ← in the corner of a whole page
   * reads as "leave this", and the step behind it is not guessable from the step you are on —
   * Payment gives no clue that it returns to Configure rather than to the plan cards.
   *
   * Each label names the DESTINATION'S CONTENT rather than the action, which is the only thing
   * that makes labelling worth doing: it tells you what you will get before you press it. That
   * rule is what forces the branches below — the same arrow reaches genuinely different pages
   * depending on how the wizard was entered, and one label cannot honestly cover them.
   */
  const backLabel = (() => {
    if (w.step === STEP.CONFIRMATION) {
      // Always Configure — it is the only step before this one, with or without a plan step.
      // `screensOnly` ("Update Screen Number") hides the period cards there, so the page behind
      // the arrow really is just the counter; promising "plan setup" would send someone back
      // looking for a plan choice that page does not render.
      return w.screensOnly ? t('Back to screen count') : t('Back to plan setup');
    }
    if (w.step === STEP.NUMBER_SCREEN) {
      // No plan step to return to (a team or sub-account already in scope), so the arrow closes
      // the wizard. Say so: an unlabelled ← that exits instead of stepping back is the one case
      // where the icon actively misleads, and the corner ✕ is the only other way out.
      if (w.skipPlanStep) return t('Close');
      // Feature-gated entry (#2044): `contextMinPlan` filters the catalog to the tiers that
      // unlock the feature clicked from — one Engage card from the Engage banner, two from a Pro
      // Plus gate. So NOT "all plans", which would promise a comparison the flow never renders,
      // and not the plan's name, which is wrong the moment the filter leaves two.
      // "Back to plans" is true of both and stays short; it was "Back to upgrade options", which
      // was accurate but the longest label in the wizard for the narrowest case.
      if (w.contextMinPlan) return t('Back to plans');
      // The full catalog. Trial, Free and lapsed accounts get this too: they reached Configure
      // through those cards exactly like a subscriber did, and the previous rule left the one
      // group most likely to still be comparing plans with an unlabelled arrow.
      return t('Back to all plans');
    }
    return undefined;
  })();

  /**
   * Charging a card already on file is a direct action — there is no Stripe form mounted for the
   * summary button to submit, so the button has to run the charge itself.
   *
   * Gated on `completesPurchaseOnThisStep`. It was once gated on `mergedPayment` — "payment has
   * been folded into the Screens step" — which was hard-false, so keying off it made this branch
   * unreachable on the Confirmation step, where payment fields are ALWAYS on screen. That merged
   * one-page purchase has since been removed outright. A paid account picking its
   * saved card and pressing Update therefore fell through to `handlePaidUpdate()`, whose whole job
   * is `setStep(STEP.CONFIRMATION)`: a no-op when you are already standing on Confirmation. Hence
   * "I click Update and nothing happens". The hook already owns this predicate for its review
   * refetches, so reusing it keeps the two from disagreeing again.
   */
  const payingWithSavedCard =
    w.completesPurchaseOnThisStep && !!w.existingCard && w.useExistingCard;

  // The redesigned header renders INSIDE the scroll area (see below) so it scrolls away with
  // the content instead of staying pinned. Deliberately NOT DialogHeader: that nests children
  // in a content-width `flex-col min-w-0` column beside its own close button, so a full-width
  // row inside it is impossible.
  // The back arrow is its OWN element in the page's top-left corner, not part of the header
  // row: the title is centred on the page, so pairing them in one row would mean giving the
  // arrow's width an empty counterweight column just to keep the title honestly centred.
  // It lives INSIDE the scroll area and scrolls away with the content.
  /**
   * Back arrow, title and currency in one compact header.
   *
   * Was three stacked bands: an absolutely-positioned arrow at `top-8`, then a header with
   * `pt-24` to clear it, then the content — ~180px burned before the first card, which the cards
   * then had to scroll to escape. This costs a single control's height.
   *
   * Both the arrow and the currency select are absolute against this row rather than flex
   * siblings of the title, for opposite reasons: the arrow sits at the PAGE's left margin (flush
   * with the scroll container's `px-8`) instead of being pulled in to the card grid's left edge,
   * while the currency rides its own `max-w-6xl` track so it lands flush with the last card's
   * RIGHT edge. Neither can nudge the title, which stays centred in its own container — and the
   * currency changes lines by breakpoint without the title having to know (see its comment).
   */
  /** The plan cards step, which gets centred-title-and-no-back-arrow treatment of its own. */
  const isPlanStep = w.step === STEP.PLAN;
  /**
   * The three states that mean "has never been invoiced", folded once. Also decides the Due Today
   * rule inside WizardStepScreens — see its `hasNoActiveSubscription` prop.
   */
  const isFirstSubscribe = w.isTrial || w.isOnFreePlan || w.isAccountInactive;
  /**
   * Configure drops its summary card, leaving one column — the decision and the reasoning live in
   * `hideSummaryCard` (WizardStepScreens). The header has to know only so the title keeps the same
   * track as the content it labels.
   */
  const isOneColumnConfigure = w.step === STEP.NUMBER_SCREEN;
  /**
   * The Payment step's track. `max-w-3xl` (768px), narrowed from `max-w-5xl` (1024px) so it does not
   * read as a wider page than the Configure step before it, which sits at `ONE_COLUMN_TRACK` (552px).
   *
   * NOT 552px: the card form is production's, and its widest row is Card number / Expiration date /
   * Security code side by side. At 552 those three crush together and the Stripe fields start
   * truncating their placeholders. 768 is the narrowest that keeps that row comfortable, so the two
   * steps read as the same page without the form fighting for space.
   */
  const isPaymentStep = w.step === STEP.CONFIRMATION;

  const redesignedHeader = (
    /**
     * LAYOUT TEST — stacked and left-aligned: back control on its own row, title beneath it, both
     * flush with the content below. Previously the title was centred with the back arrow and the
     * currency select absolutely positioned on top of it.
     *
     * To revert: restore the `relative py-6` wrapper, put `absolute left-0 top-4` back on the
     * button, restore the currency select's own `absolute inset-x-0 top-4 lg:top-16` track, and
     * put `text-center` back on the DialogTitle inside an `mt-10` container.
     *
     * What this buys, beyond taste: the whole header is now in normal flow, so the three
     * absolute-position workarounds go away — the arrow's fixed `top-4`, the select's two-height
     * `top-4 / lg:top-16` track (which existed because a centred title collided with it below
     * ~1216px), and the `pointer-events-none` band that stopped that track swallowing clicks.
     */
    /*
     * `pt-20 pb-0` on the plan step: 80px of air above the title, MATCHED to where the other steps
     * put their heading rather than derived from taste.
     *
     * The other steps land their title at pt-6 (24) + back-control row `h-10` (40) + title `mt-2`
     * (8) = 72px. The plan step has no back row — it is the first step — so it makes up the
     * difference in padding. It was `pt-12` for a while, which put the heading 32px higher than
     * every following step, so stepping forward visibly jogged it downward. Once the cards lost
     * their artwork the whole step also sat high on the page, which is what made the mismatch
     * obvious.
     *
     * 80 against 72 is deliberate and not an oversight: `pt-20` is the nearest Tailwind step, and
     * an 8px difference is invisible where a 32px one was not. If either number moves, move both.
     *
     * `pb-0` on the plan step against `pb-6` elsewhere. It was `pb-0`, paired with a `lg:-mt-12`
     * lift on the currency row, to claw back every pixel between the title and the cards — which
     * overshot: the title ended up sitting almost on top of the grid, and the currency select was
     * riding up onto the title's own line to make that possible. The gap is deliberate space now,
     * declared here plus the currency row's own `mb-4`, and it is the SAME gap at every viewport
     * height (see the plan step's content wrapper, which no longer centres itself).
     * Bottom padding stays small whatever the top does: the title labels the cards, so the space
     * between them is the one that has to read as tight.
     */
    /*
     * `pb-0` on the plan step: the header's own bottom padding was the one part of the
     * subtitle-to-cards gap that bought nothing. The currency row below declares its own spacing,
     * and the subtitle is what the cards need to sit close to.
     *
     * `pt-20` (80px), measured rather than picked: it puts the plan step's title on the SAME
     * baseline as the Configure step's, so stepping forward does not shift the heading. Configure
     * reaches 80px as `pt-6` plus its back-arrow row; the plan step is the wizard's first step and
     * has no back arrow, so it has to make up the 32px in padding. Was `pt-12`, which was fine
     * while the cards carried artwork and filled the viewport — with the artwork gone the whole
     * step sat high and the two steps visibly jumped.
     * The dialog's own X is positioned against the dialog, not this wrapper, so it stays put.
     */
    <div className={cn(isPlanStep ? 'pt-20 pb-0' : 'pt-6 pb-6')}>
      <div
        className={cn(
          'mx-auto',
          isPlanStep
            ? 'max-w-6xl'
            : isOneColumnConfigure
              ? ONE_COLUMN_TRACK
              : isPaymentStep
                ? 'max-w-3xl'
                : 'max-w-5xl',
        )}
      >
        {/* The back-control row, rendered only when there IS one. On the PLAN step there is not:
            it is the wizard's first step, so "back" there only ever meant "close", and now the
            dialog draws its own X on every step an arrow doing the same job is a second exit
            wearing a different icon. The currency select moved into the step itself, so on that
            step this row had no children at all — an empty flex box whose only remaining effect
            was to push the title down by its own sibling margin.
            Later steps keep the control, where it genuinely steps backwards, and keep the label
            because it is the only signpost to the plan cards: the Subscription Plan page has no
            surfaced "change plan" entry, so "Back to all plans" here IS how you reach them. */}
        {!isPlanStep && (
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              aria-label={backLabel ?? t('Close')}
              data-testid="wizard-back-btn"
              className="group inline-flex cursor-pointer items-center gap-3"
              onClick={() => (canGoBackAStep ? w.goBack() : w.handleOpenChange(false))}
            >
              {/* No resting fill: at rest the arrow sits directly on the dialog, and the circle only
                paints on hover. A permanent grey disc on the near-white dialog read as a filled
                control competing with the step's real buttons.
                Hover is `panel-hover` (#eceef6), NOT the usual `accent` (#f7f7fb) — accent is
                within a hair of the dialog's own surface, so with no resting fill to change FROM
                the hover was invisible.
                `-ml-2.5` pulls the circle's own padding out of the flow so the ARROW, not the
                disc, lines up with the title's left edge. */}
              <span className="-ml-2.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors group-hover:bg-panel-hover">
                <ArrowLeft className="h-5 w-5 text-foreground" />
              </span>
              {/* Label stays `text-foreground` on hover — the circle behind the arrow already gives
                the control its hover feedback, and turning the label green read as a CTA. */}
              {backLabel && (
                <span className="-ml-2.5 text-sm font-semibold text-foreground">{backLabel}</span>
              )}
            </button>
          </div>
        )}
        {/* Centred on the PLAN step, left-aligned after it.
            The plan step is a three-card row with no single anchor, so a centred title sits over
            the middle card and belongs to the whole set. Later steps are a two-column layout whose
            content starts hard left, and a centred title there floated over the gap between the
            columns rather than heading either of them. Both share the container that sets the
            content width, so "left" means the same edge the cards start on. */}
        {/* A top margin only where a back row sits above it. On the plan step the title is the
            first thing in the header, so the margin was spacing it away from nothing — that plus
            the wrapper's full `pb-6` is what left it floating well clear of the cards.
            `mt-2` (8px), down from `mt-4`: the back control is a 40px circle whose glyph is
            optically centred, so it already carries visual padding below it. At 16px the gap
            measured tight but read as a break between two unrelated things rather than a label
            above its own heading. */}
        <DialogTitle
          className={cn('text-3xl font-semibold', isPlanStep ? 'text-center' : 'mt-2')}
          data-testid="wizard-step-title"
        >
          {redesignedTitle}
        </DialogTitle>
        {/* Which trial you are on, and how long it has left — under the title, on the plan step
            only.
            It used to be a check + "Your trial plan" beside the plan name on the recommended card.
            That put an account-specific fact inside a card whose job is describing a TIER, made the
            trialled card taller than its neighbours, and said nothing about time. Here it reads as
            one line of context for the whole set, which is what it is.
            Deliberately just the fact: the title already says "Select Your Plan", so a "choose a
            plan" clause after it would be the same instruction twice. */}
        {isPlanStep && trialSubtitle && (
          <p
            // `text-foreground`, not `text-muted-foreground` (#6c757d): this line is the answer to
            // "which plan am I on and how long have I got", which is worth reading, and at muted
            // grey under a `text-3xl` title it read as fine print.
            className="mt-2 text-center text-sm text-foreground"
            data-testid="wizard-trial-subtitle"
          >
            {trialSubtitle}
          </p>
        )}
      </div>
    </div>
  );

  // ---- Render step content ----
  const renderStepContent = () => {
    switch (w.step) {
      case STEP.NUMBER_SCREEN:
        return (
          <WizardStepScreens
            screenCount={w.screenCount}
            onScreenCountChange={w.setScreenCount}
            minScreens={w.minScreens}
            // Licenses on the subscription today — the "originally N" baseline.
            currentScreens={w.maxScreens ?? 0}
            usedScreens={w.usedScreens}
            selectedPlan={w.selectedPlan}
            currency={w.currency}
            billingInterval={w.billingInterval}
            onBillingIntervalChange={w.setBillingInterval}
            monthlyPrice={w.monthlyPrice}
            yearlyPrice={w.yearlyPrice}
            // The summary button owns the forward move now the footer is gone here. The label
            // has to match what the click actually does: with payment merged onto this page it
            // Payment has its own step, so this button only ever advances. It used to read
            // "Subscribe" / "Update" when the purchase completed here — promising "Subscribe" and
            // then showing a payment form is the version users complain about.
            primaryLabel={t('Continue to payment')}
            primaryDisabled={!w.canGoNext || !w.planFeatureId || w.isSubmitting}
            // Only reached when the button is NOT submitting an inline Stripe form — i.e.
            // paying with a saved card, or no payment on this step at all.
            onPrimary={() => {
              // #1113 — a paid account reducing to 0 screens is a cancellation; route it
              // through the survey rather than charging for nothing.
              if (shouldRouteToCancelOnUpdate) {
                setShowCancelDialog(true);
                return;
              }
              if (payingWithSavedCard) {
                w.handleConfirmExistingCard();
                return;
              }
              // No payment on this step (team billing / free-device bundles): hand off to the
              // Payment step, which owns those flows.
              if (w.isTrial) {
                w.goNext();
              } else {
                w.handlePaidUpdate();
              }
            }}
            // Paid users lose the footer's Cancel Subscription button on the redesigned steps, so
            // it reappears in the summary as a quiet link rather than vanishing.
            onCancelSubscription={showCancelButton ? () => setShowCancelDialog(true) : undefined}
            // --- Inline payment (merged Screens + Payment page) ---
            // Totals stay on this page either way — see `showTotals` in WizardStepScreens.
            showTotals
            onPreparePayment={() => w.fetchConfirmationData({ includeAddonTotal: true })}
            promoCode={w.promoCode}
            onApplyPromo={w.handleApplyPromo}
            onRemovePromo={w.handleRemovePromo}
            discountLabel={w.discountLabel}
            accountDiscount={w.accountDiscount}
            discountPercentOff={w.discountPercentOff}
            discountAmountOff={w.discountAmountOff}
            // Prorated review, so Due Today in the summary is the amount actually charged.
            // #1411 / #1413 — trial, Free plan and cancelled accounts have no subscription to
            // prorate from, so the backend returns 0 and the subtotal is the real charge.
            hasNoActiveSubscription={isFirstSubscribe}
            reviewTotal={w.reviewResult?.total ?? 0}
            reviewSubtotal={w.reviewResult?.subtotal ?? null}
            reviewTax={w.reviewResult?.tax ?? null}
            reviewTaxs={w.reviewResult?.taxs ?? null}
            currentPlanAlias={w.currentPlanFeature ?? undefined}
            nextChargeSubtotal={w.reviewResult?.newMonthlySubtotal ?? null}
            nextChargeTotal={w.reviewResult?.newMonthlyTotal ?? null}
            nextChargeTax={w.reviewResult?.newMonthlyTax ?? null}
            nextChargeBreakdown={w.reviewResult?.newMonthlyBreakdown ?? null}
            reviewLoading={w.reviewLoading}
            initialCredit={w.initialCredit}
            renewalDate={w.renewalDate}
            currentPeriodEnd={w.currentPeriodEnd}
            addonTotal={w.addonTotal}
            // Only surface Screen Management when there are paired screens
            // to unpair. With 0 paired screens, hitting min=1 should just
            // disable the - button — opening a "remove screens to reduce"
            // dialog with "No devices found" is confusing.
            //
            // GitLab #1324 — when parent-managing-sub-account context is
            // active (`contextSubAccountId` set), don't open Screen Management
            // either: the backend cross-account check
            // (device.service.ts: `device.accountId !== authContext.accountId`)
            // rejects parent's unpair attempt on sub-account screens. scio1
            // parity: parent simply cannot reduce below the sub-account's
            // paired count — `-` stays silently disabled at the floor.
            // "Update Screen Number" hides the period cards + plan pitch — see WizardStepScreens.
            screensOnly={w.screensOnly}
            onDecreaseBlocked={
              w.usedScreens > 0 && !w.contextSubAccountId
                ? () => w.setShowScreenManagement(true)
                : undefined
            }
            teamMode={!!w.contextTeamId}
            teamName={w.teamNameInput}
            onTeamNameChange={w.setTeamNameInput}
            teamDescription={w.teamDescriptionInput}
            onTeamDescriptionChange={w.setTeamDescriptionInput}
          />
        );

      case STEP.PLAN:
        return (
          <WizardStepPlan
            /* The term toggle drives the wizard's REAL `billingInterval`, not a preview copy of
               it: the Configure step's period cards read the same value, so a term chosen here is
               already selected there. A display-only toggle would have shown a yearly price and
               then landed the user on a monthly Configure. */
            billingInterval={planTermTouched ? w.billingInterval : BILLING_INTERVAL.MONTHLY}
            onBillingIntervalChange={(interval) => {
              setPlanTermTouched(true);
              w.setBillingInterval(interval);
            }}
            /* Currency is picked once, directly above the prices it changes, and the choice
               carries through the rest of the wizard — repeating the control on later steps just
               invites a change after the decision it affects has been made.
               An existing subscription fixes the currency: `locked` renders read-only text plus an
               explanation rather than a dead dropdown (see CurrencySelect), and it renders in the
               same slot so the row does not move between the two states. */
            currencySelect={
              <CurrencySelect
                value={w.currency}
                onChange={w.setCurrency}
                locked={!w.canChangeCurrency}
              />
            }
            onPlanChange={w.handleSelectPlan}
            // Marks the chosen card and gates the Next button below the grid.
            selectedPlan={w.selectedPlan}
            validating={w.validatingPlan}
            // Each card's CTA selects and advances — no footer Next on this step. Must go
            // through goNext(), NOT setStep: leaving the plan step runs validateChangePlan,
            // which raises the downgrade-warning and teams-not-supported dialogs (#1275,
            // #1495). setStep would skip those guards entirely.
            onContinue={w.goNext}
            onSelectFree={w.handleSelectFree}
            selectingFree={w.isSelectingFree}
            // Pass the REAL current plan (no pro→proPlus normalization). The retired `pro`
            // card is hidden (isActive=false), so this won't highlight any card for a legacy
            // pro account — and crucially it does NOT mark the Pro Plus card as "Current Plan"
            // (which would make it unselectable and re-block the upgrade).
            currentPlan={w.currentPlanFeature ?? undefined}
            // Decides how the card for the CURRENT plan behaves: a trial shows the
            // "Current trial" chip and a live Select button; a subscriber gets an inert
            // "Your current plan" CTA and no chip. See `isTrial` in PlanCardGrid.
            isTrial={w.isTrial}
            currency={w.currency}
            minPlanRequired={w.contextMinPlan}
            // SP45 — hide the Free row when the user's effective plan is
            // already Free (single source of truth lives in useSubscriptionWizard).
            // Expired-trial accounts carry `isTrial=true` but planFeature=`'free'`,
            // so they still hide. Pro Plus trial users keep it visible.
            hideFreePlanOption={w.isOnFreePlan}
          />
        );

      case STEP.CONFIRMATION:
        /**
         * Production's Payment step, on the experiment's full-screen page.
         *
         * Arm B's redesign of this step (a hand-built card form plus the Order Summary sidebar,
         * rendered by `WizardStepScreens paymentOnly`) is out of scope for this round and is
         * parked on `feature/jessica/sub-ui-payment-step-parked` — see
         * claude-dev/jessica-dev/2026-08-14-sub-ui-update/03-parked-payment-step.md for what it
         * held and how to reinstate it. Until then this renders exactly what every other user
         * sees, so nothing unreviewed reaches the arm; only the page around it is the new one.
         *
         * The prop set is production's, verbatim (dialogs/SubscriptionWizardDialog.tsx).
         * `hideTitle` is the single addition: the shell above already says "Payment".
         */
        return (
          /* The SAME track the header rides for this step (`max-w-3xl` — see `isPaymentStep`), so
             the fields line up under the "Payment" title. Production renders this step inside an
             `sm:max-w-6xl` dialog, which bounded it for free; the experiment's shell is the whole
             viewport, so without a track the form stretches edge to edge and starts a third of a
             screen left of its own heading. The Plan and Configure steps declare theirs
             internally. */
          <div className="mx-auto w-full max-w-3xl">
            <WizardStepConfirmation
              hideTitle
              inPageSubmit
              isTrial={w.isTrial}
              // #1411 — free-plan upgraders have no active subscription to prorate,
              // so "Due Today" must mirror the Subtotal (the amount actually charged).
              isFreePlan={w.isOnFreePlan}
              // #1413 — a cancelled/inactive account re-subscribing is the same case:
              // no active subscription → backend review returns total=0 even though the
              // card is charged the full amount. Treat like trial/free for "Due Today".
              isAccountInactive={w.isAccountInactive}
              existingCard={w.existingCard}
              useExistingCard={w.useExistingCard}
              onUseExistingCardChange={w.setUseExistingCard}
              stripeLoading={w.stripeLoading}
              stripeClientSecret={w.stripeClientSecret}
              stripePaymentMethodOrder={w.stripePaymentMethodOrder}
              stripeReturnUrl={w.stripeReturnUrl}
              defaultEmail={w.defaultEmail}
              onStripeSuccess={w.handleStripeSuccess}
              isSubmitting={w.isSubmitting}
              billingInterval={w.billingInterval}
              currentPricePerScreen={w.currentPricePerScreen}
              screenCount={w.screenCount}
              onScreenCountChange={w.setScreenCount}
              minScreens={w.minScreens}
              originalScreenCount={w.maxScreens}
              disableScreenCount={!!w.contextMinPlan}
              addonTotal={w.addonTotal}
              addonItems={w.addonItems}
              currency={w.currency}
              selectedPlan={w.selectedPlan}
              planSummaryName={getPlanDisplayName(w.submitPlanAlias)}
              planEditName={
                w.aliasPlanData?.[w.currency]?.[w.billingInterval]?.name as string | undefined
              }
              planEditDiscount={
                w.aliasPlanData?.[w.currency]?.[w.billingInterval]?.discount as number | undefined
              }
              reviewResult={w.reviewResult}
              reviewLoading={w.reviewLoading}
              initialCredit={w.initialCredit}
              onConfirmExistingCard={w.handleConfirmExistingCard}
              promoCode={w.promoCode}
              onPromoCodeChange={w.setPromoCode}
              showPromoCode={w.showPromoCode}
              onShowPromoCode={w.setShowPromoCode}
              onBillingAddressChange={w.handleBillingAddressChange}
              onStripeSubmitStart={() => w.setStripeProcessing(true)}
              onStripeSubmitError={() => w.setStripeProcessing(false)}
              teamMode={!!w.contextTeamId}
              editedDeviceFrees={w.editedDeviceFrees}
              onInitDeviceFrees={w.initEditedDeviceFrees}
              onBundleQuantityChange={w.setBundleQuantity}
            />
          </div>
        );

      case STEP.FINISH:
        return (
          <WizardStepFinish
            chargeResult={w.chargeResult as Parameters<typeof WizardStepFinish>[0]['chargeResult']}
            currency={w.currency}
            currencySymbol={w.currencySymbol}
            currentCard={w.existingCard}
            onClose={() => w.handleOpenChange(false)}
          />
        );
    }
  };

  /**
   * Every successful charge ends on a compact confirmation rather than the full-page receipt —
   * a first subscribe and a plan/quantity change alike. `SubscribedThankYou` adapts its copy and
   * its money row to the `finishAction`.
   *
   * ONE exception stays on the full page: an order-device coupon. That outcome carries a second
   * decision ("Continue Later" plus the coupon itself) which a single-action popup would either
   * hide or misrepresent.
   *
   * Gated on the charge's own `finishAction`, not on `isTrial` / `isAccountInactive`: those are
   * live store values that the post-charge `refetchQueries` flips WHILE this is still on screen,
   * so reading them here would change the UI out from under the user. `chargeResult` is a
   * snapshot of what actually happened.
   */
  const chargeResult = w.chargeResult as
    | (SubscribedChargeResult & {
        orderDeviceCoupon?: unknown;
        orderDeviceCouponNumber?: unknown;
      })
    | null;
  /**
   * The compact receipt now serves the ORDER-DEVICE-COUPON path too. It used to be excluded
   * (`&& !hasOrderDeviceCoupon`) because the "Order Your Free Device" CTA existed only on the
   * full-page `WizardStepFinish`, so a purchase that earned a device fell back to a heavier
   * receipt than one that did not. `SubscribedThankYou` carries that CTA now, so both paths get
   * the same dialog, and this file no longer needs to know about the coupon at all.
   */
  const showThankYouDialog = w.step === STEP.FINISH && !!chargeResult;

  /**
   * Covers the hand-off to the fresh document after a subscribe.
   *
   * The wizard deliberately leaves via a full page load — that is what lifts the paywall, since it
   * re-reads `me` and drops every Apollo query and company-scoped store. What looked broken was
   * the seam: the dialog used to close first, so the old (still paywalled) portal painted for a
   * beat, then the browser tore it down and reloaded. This sits above everything until the new
   * document paints, so the user sees one calm surface instead of a flash of the page they were
   * just rescued from. It cannot be dismissed and needs no exit animation — the document is
   * replaced out from under it.
   */
  if (w.isLeavingToLanding) {
    return (
      <div
        className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3 bg-background"
        data-testid="wizard-leaving-veil"
      >
        <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{t('Setting up your account...')}</p>
      </div>
    );
  }

  if (showThankYouDialog) {
    return (
      /**
       * `key` is load-bearing, not decoration. Both branches of this component return
       * Dialog > DialogContent at the same tree position, so without distinct keys React reuses
       * the SAME DOM node and only swaps its className — from the full-page takeover
       * (`left-0 top-0 w-screen h-dvh translate-x-0`) to this centred card. The shared
       * DialogContent base carries `duration-200` with no `transition-property`, whose CSS
       * initial value is `all`, so the browser transitioned left/top/transform/width/height and
       * the confirmation visibly flew in from the top-left corner. Distinct keys force a real
       * unmount + mount, so it appears centred and plays Radix's normal zoom/fade instead.
       */
      <Dialog key="thank-you" open={w.isOpen} onOpenChange={w.handleOpenChange}>
        {/* `focus:outline-none` on the CONTAINER, and only here.

            Radix moves focus onto the content element when the dialog opens. Every other dialog
            in the app opens from a click, so `:focus-visible` does not match and the browser
            paints nothing. This one now opens on page load (the post-subscribe handoff re-shows
            it on the fresh document), where there has been no pointer interaction in that
            document yet — so the heuristic treats it as keyboard focus and draws its default
            blue ring around the whole modal.

            Safe to suppress: the container is not a control, it is a focus anchor. It keeps its
            `tabIndex={-1}` so screen readers still announce the dialog on open, and Tab still
            reaches the close button and the actions inside, each with their own ring. */}
        <DialogContent
          /* `max-w-md` (448px) on both paths. It was briefly 512 to stop the hardware band feeling
             cramped, but that was a symptom of the heading holding one long line: letting it wrap
             to two frees the width instead, so the dialog can go back to the tighter shell that
             suits a three-row receipt. (It was also `max-w-2xl` for a while, when the offer was a
             right-hand column rather than a band.)
             One width for both paths deliberately: the offer is a full-width band, so whether it
             renders does not change how much horizontal room the dialog needs. */
          className="max-w-md focus:outline-none"
          data-testid="subscribed-thank-you-dialog"
        >
          <SubscribedThankYou chargeResult={chargeResult} currency={w.currency} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    // Keyed to stay distinct from the confirmation dialog above — see the note there.
    <Dialog key="wizard" open={w.isOpen} onOpenChange={handleWizardOpenChange}>
      {/* Full-page takeover, not a modal card. Choosing a plan is the only task on screen,
          so the surface fills the viewport: no scrim, no rounded card, no visible portal
          behind it.

          It stays a Dialog rather than becoming a route so every existing entry point keeps
          working untouched — the lapsed-account paywall, PlanGate on 11 pages, plan-floor
          errors, the designer bridge and SubscriptionPage all open it through
          `billing-dialog-store`. The trade-off is no URL of its own: no deep link, and the
          browser Back button leaves the app instead of stepping back (the in-page back arrow
          does that). Worth revisiting if this needs to be linkable.

          The class list neutralises DialogContent's centred-card positioning: its
          `left/top-[50%]` + `translate-[-50%]` anchoring, max width, radius, border, shadow
          and the slide-in animation (kept as a fade/zoom, which reads fine at full size). */}
      <DialogContent
        className={cn(
          'left-0 top-0 h-dvh w-screen max-w-none translate-x-0 translate-y-0',
          // bg-muted, not bg-background: the cards are bg-card (white), so a white page gave
          // them nothing to sit against and their borders were doing all the work.
          'flex flex-col overflow-hidden rounded-none border-0 p-0 shadow-none bg-muted',
          'data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0',
          'data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0',
          // Bigger close X — 40px target with a 20px glyph, up from the shared button's 32/16.
          // Scoped HERE rather than changed in `packages/ui`: that button is the corner X of every
          // dialog in the app, and this is a full-viewport takeover where the default reads small
          // against a whole page of content rather than a card.
          // Reached by attribute selector because DialogContent exposes no prop for it; the button
          // is a direct child of the content element and carries this testid.
          '[&>[data-testid=dialog-x-close-btn]]:h-10 [&>[data-testid=dialog-x-close-btn]]:w-10',
          '[&>[data-testid=dialog-x-close-btn]_svg]:h-5 [&>[data-testid=dialog-x-close-btn]_svg]:w-5',
        )}
        // No dimmed backdrop to look through — the page IS the wizard. Matches the surface so
        // no seam shows during the open/close fade.
        overlayClassName="bg-muted"
        // Every step keeps its corner X. It used to be hidden on the redesigned Plan and Screens
        // steps on the theory that the header's back arrow was exit enough — but the arrow means
        // "back" everywhere else in the wizard and only doubles as "close" on the very first step,
        // so on the Screens step there was no visible way out at all without stepping backwards
        // first. `onInteractOutside`/`onEscapeKeyDown` are both prevented here too, which left
        // the step with no escape hatch of any kind.
        hideCloseButton={false}
        data-testid="subscription-wizard-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        /**
         * #2043 — no programmatic focus on mount.
         *
         * Radix `FocusScope` focuses the first tabbable descendant when the dialog opens. On the
         * Plan step that descendant is the locked-currency ⓘ button: the step renders no back
         * arrow, the shared ✕ is appended AFTER the content, and the plan cards are still
         * loading — so the ⓘ is the only focusable node in the dialog. Radix Tooltip opens on
         * FOCUS, so a black "Your billing currency is set by your subscription and cannot be
         * changed" box appeared by itself in the middle-right of an otherwise empty page, then
         * vanished once the cards rendered and focus moved.
         *
         * Preventing the default only removes the PROGRAMMATIC focus. Tab still reaches the ⓘ
         * and still opens the explanation, so the tooltip stays keyboard-accessible; the focus
         * trap still holds, because FocusScope pulls focus back inside on any focusin outside.
         * `InactiveAccountDialog.tsx` (same feature, same tooltip-on-mount bug) and
         * `ScreenManagementDialog` already do this — the wizard just never adopted it.
         *
         * No explicit initial target here, unlike InactiveAccountDialog's "Choose plan" button:
         * this dialog has four steps whose first meaningful control differs, and on the Plan step
         * the one worth focusing (a plan card) does not exist yet at mount.
         */
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {usesRedesignedChrome ? null : (
          // Payment / Finish keep the original header. The plan-step subtitles that used to
          // live here ("You are currently on the X plan", the upgrade pitch) are gone: the
          // plan step always uses the redesigned header above, which is title-only per the
          // design, so those branches were unreachable. `contextTitle` / `contextMinPlan`
          // still steer the redesigned title itself.
          <DialogHeader className="px-6 pt-5 pb-4 space-y-0.5 bg-transparent">
            <DialogTitle className="text-xl font-semibold">
              {w.contextTitle ??
                (w.contextMinPlan ? t('Upgrade Required') : t('Update Subscription Plan'))}
            </DialogTitle>
          </DialogHeader>
        )}

        {/* Step indicator — centered on its own row below the header. Hidden on FINISH,
            for paid users, and on the redesigned steps (which carry their own header). */}
        {w.step !== STEP.FINISH && w.isTrial && !usesRedesignedChrome && (
          <div className="px-6 pt-1 pb-2">
            {/* scio1-parity progress bar: each step renders [left line | circle | right line]
                with the lines flex-growing inside an equal-width column. Adjacent steps' line
                halves butt together to form a single continuous bar that passes flush against
                each circle's edge, exactly like scio1's wizard.
                Color rule: the segment between step N and N+1 turns green once the user has
                moved past step N (activeIndicatorIndex > N). The line entering step 0 is
                always green so the user gets a "you've started the flow" cue. */}
            <div className="flex items-start justify-center max-w-md mx-auto">
              {STEP_INDICATORS.map((indicator, idx) => {
                const Icon = indicator.icon;
                const isDone = activeIndicatorIndex > idx;
                const isCurrent = activeIndicatorIndex === idx;
                const isDisabled = activeIndicatorIndex < idx;
                const isFirst = idx === 0;
                const isLast = idx === STEP_INDICATORS.length - 1;
                // Left half of segment idx-1 → idx: green once user has reached idx.
                const leftLineColor = activeIndicatorIndex >= idx ? 'bg-primary' : 'bg-slate-300';
                // Right half of segment idx → idx+1: green once user has moved past idx.
                const rightLineColor = activeIndicatorIndex > idx ? 'bg-primary' : 'bg-slate-300';

                return (
                  <div key={indicator.step} className="flex flex-col items-center flex-1 min-w-0">
                    <div className="flex items-center w-full">
                      {/* Left half of the connector — hidden on the first step */}
                      <div className={cn('h-1 flex-1', isFirst ? 'invisible' : leftLineColor)} />
                      <div
                        className={cn(
                          'flex items-center justify-center rounded-full border-2 h-10 w-10 shrink-0',
                          isCurrent || isDone
                            ? 'border-primary bg-primary text-white'
                            : 'border-slate-300 bg-card text-muted-foreground',
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      {/* Right half of the connector — hidden on the last step */}
                      <div className={cn('h-1 flex-1', isLast ? 'invisible' : rightLineColor)} />
                    </div>
                    <span
                      className={cn(
                        'mt-1 text-xs whitespace-nowrap',
                        isCurrent
                          ? 'text-primary font-bold'
                          : isDisabled
                            ? 'text-muted-foreground'
                            : 'text-foreground',
                      )}
                    >
                      {t(indicator.label)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <ProcessingOverlay visible={w.isSubmitting} />

        {/* Step content — show loader while sub-account data loads.
            pb-24 reserves space for the floating footer; the redesigned steps have no footer,
            so they drop it rather than leave a dead band under the content. */}
        <div
          className={cn(
            // min-h-0 is what makes the page scroll. A flex child defaults to
            // `min-height: auto`, so without it this refuses to shrink below its content,
            // grows past the h-dvh DialogContent, and that parent's overflow-hidden clips
            // the overflow instead of this element scrolling it.
            // `relative` is kept for the step content's own absolutely-positioned bits (the
            // back arrow moved into the header row and no longer needs it).
            'relative flex min-h-0 flex-1 flex-col overflow-y-auto',
            // Redesigned steps: page-style margins and a normal bottom pad. Was `pb-40`, a
            // 160px dead band that pushed every step past the viewport and made all three
            // scroll by default; overflow-y-auto above still scrolls whenever content
            // genuinely exceeds the height.
            usesRedesignedChrome ? 'px-8 pb-8' : 'px-10 pb-24 pt-8',
          )}
        >
          {usesRedesignedChrome && redesignedHeader}
          {/* Every step's content starts directly under the header — NOT vertically centred.
              The plan step used to carry `my-auto`, which centred the cards in whatever space was
              left over. That made the one measurement people actually read — how far the cards sit
              below the title — a function of window height: roomy on a tall monitor, touching the
              title on a laptop, and different again with the browser's dev tools open. The gap is
              now declared once (the header's `pb-4` plus the currency row's `mb-4`) and is the same
              at every size, which is also what lets the title keep the fixed baseline it shares
              with the next step's heading.
              (Centring was also why the locked-currency tooltip in #2043 flashed in the vertical
              middle of an empty page: the block it belonged to was floating there.) */}
          <div className="w-full">
            {w.loadingSubAccountData ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              renderStepContent()
            )}
          </div>
        </div>

        {/* Footer with navigation — floats over the scroll area with a soft upward shadow.
            Suppressed on the redesigned steps, whose actions live in the content (card CTAs,
            summary button) and whose Back sits in the header. */}
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 py-4 bg-card shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-10',
            usesRedesignedChrome && 'hidden',
          )}
        >
          <div className="flex items-center gap-2">
            {showCancelButton && w.step === STEP.NUMBER_SCREEN && (
              <Button
                variant="outline"
                className="cursor-pointer text-destructive border-destructive hover:bg-destructive/10"
                onClick={() => setShowCancelDialog(true)}
                data-testid="wizard-cancel-subscription-btn"
              >
                {t('Cancel Subscription')}
              </Button>
            )}
            {w.stepIndex > 0 &&
              w.step !== STEP.FINISH &&
              !(w.contextMinPlan && w.step === STEP.PLAN) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!w.isTrial && w.step === STEP.CONFIRMATION) {
                      if (w.skippedToConfirmation) {
                        w.setSkippedToConfirmation(false);
                        w.setStep(STEP.NUMBER_SCREEN);
                      } else {
                        w.setStep(STEP.PLAN);
                      }
                    } else {
                      w.goBack();
                    }
                  }}
                  className="cursor-pointer"
                  data-testid="wizard-prev-btn"
                >
                  {t('Previous')}
                </Button>
              )}
          </div>
          <div className="flex items-center gap-2">
            {/* Footer Update Subscription button on CONFIRMATION step.
                The Summary sidebar inside WizardStepConfirmation owns this button
                in the paid layout (#1145), but the *trial* layout has no sidebar.
                Render the footer button only when the sidebar won't — i.e. when
                `isShowConfirm = !isTrial || !!existingCard` is FALSE: trial user
                with no card on file. Covers BOTH the fresh-trial-signup flow
                AND the Trial→Free upgrade-required flow (#1181 — `payment.isTrial`
                lingers `true` after downgrade, so the wizard renders the trial
                layout there too). Without this, those users hit a dead end on
                the Payment step (regression of #1145). */}
            {w.step === STEP.FINISH ? null : w.step === STEP.CONFIRMATION ? (
              w.isTrial && !w.existingCard ? (
                w.useExistingCard ? (
                  <Button
                    onClick={w.handleConfirmExistingCard}
                    disabled={w.isSubmitting || w.reviewLoading}
                    className={cn(
                      w.isSubmitting || w.reviewLoading
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer',
                    )}
                    data-testid="wizard-submit-btn"
                  >
                    {w.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('Processing...')}
                      </>
                    ) : (
                      t('Update subscription')
                    )}
                  </Button>
                ) : (
                  <Button
                    // Plain button + explicit submit, not `type="submit" form=...`: this footer
                    // slot holds the Customize step's "Next" button one render earlier, so React
                    // reuses the node and a native submitter would fire on the click that merely
                    // brought the user here. See STRIPE_FORM_ID in StripeCardForm.
                    type="button"
                    onClick={submitStripeForm}
                    disabled={w.isSubmitting || w.stripeProcessing || !w.stripeClientSecret}
                    className={cn(
                      w.isSubmitting || w.stripeProcessing || !w.stripeClientSecret
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer',
                    )}
                    data-testid="wizard-submit-btn"
                  >
                    {w.isSubmitting || w.stripeProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('Processing...')}
                      </>
                    ) : (
                      t('Update subscription')
                    )}
                  </Button>
                )
              ) : null
            ) : !w.isTrial && w.step === STEP.NUMBER_SCREEN ? (
              <Button
                onClick={() => {
                  if (shouldRouteToCancelOnUpdate) {
                    setShowCancelDialog(true);
                  } else {
                    w.handlePaidUpdate();
                  }
                }}
                disabled={!w.canGoNext || !w.planFeatureId}
                className={cn(
                  !w.canGoNext || !w.planFeatureId
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                data-testid="wizard-update-btn"
              >
                {t('Update')}
              </Button>
            ) : w.contextMinPlan && w.step === STEP.PLAN ? (
              <Button
                onClick={() => w.goNext()}
                disabled={
                  !w.canGoNext ||
                  w.validatingPlan ||
                  (!w.isTrial && w.selectedPlan === w.currentPlanFeature)
                }
                className={cn(
                  !w.canGoNext ||
                    w.validatingPlan ||
                    (!w.isTrial && w.selectedPlan === w.currentPlanFeature)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                data-testid="wizard-change-btn"
              >
                {w.validatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('Checking...')}
                  </>
                ) : (
                  t('Change')
                )}
              </Button>
            ) : (
              <Button
                onClick={() => w.goNext()}
                disabled={!w.canGoNext || w.validatingPlan}
                className={cn(
                  !w.canGoNext || w.validatingPlan
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                data-testid="wizard-next-btn"
              >
                {w.validatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('Checking...')}
                  </>
                ) : (
                  t('Next')
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Screen Management dialog — opens when user tries to decrease below min */}
      <ScreenManagementDialog
        open={w.showScreenManagement}
        onClose={() => w.setShowScreenManagement(false)}
        totalPairedScreens={w.minScreens}
        subAccountId={w.contextSubAccountId}
        teamId={w.contextTeamId}
      />

      {/* Asked when the corner X is pressed on the Payment step — see handleWizardOpenChange */}
      <LeaveCheckoutDialog
        open={showLeaveCheckout}
        onStay={() => setShowLeaveCheckout(false)}
        onLeave={() => {
          setShowLeaveCheckout(false);
          w.handleOpenChange(false);
        }}
      />

      {/* Plan change warning dialog */}
      <ChangePlanWarningDialog
        open={w.showChangePlanWarning}
        warnings={w.changePlanWarnings}
        confirmLabel={w.changePlanWarningConfirmLabel}
        // Column headings for the before/after table. `getPlanDisplayName` normalises the alias
        // casing mismatch (catalog `proPlus` vs payment `proplus`) that a raw alias would expose.
        currentPlanName={
          w.currentPlanFeature ? getPlanDisplayName(w.currentPlanFeature) : undefined
        }
        newPlanName={w.selectedPlan ? getPlanDisplayName(w.selectedPlan) : undefined}
        onDismiss={w.dismissChangePlanWarning}
        onConfirm={w.confirmChangePlanWarning}
      />

      {/* #1275 — block downgrading to a no-Team plan (Free/Standard) from inside a non-default team */}
      <TeamPlanBlockDialog
        open={w.teamPlanBlockOpen}
        onDismiss={w.dismissTeamPlanBlock}
        onSwitchToDefault={w.switchToDefaultTeam}
      />

      {/* TypeForm cancel subscription dialog — fires `cancelSubscription`
          mutation + refetches GetCompanyInfo internally (#1113), so by the time
          this onClose runs the cache is already fresh. We only need to close
          the wizard; no setTimeout, no duplicate refetch. */}
      <CancelSubscriptionDialog
        open={showCancelDialog}
        onClose={(submitted) => {
          setShowCancelDialog(false);
          if (submitted) {
            w.handleOpenChange(false);
          }
        }}
        subAccountId={w.contextSubAccountId}
        onSwitchPlans={() => {
          setShowCancelDialog(false);
          // Use the cancel-flow handoff (not raw setStep) so picking Free
          // from the PLAN step skips the redundant DowngradeSurveyDialog.
          w.enterPlanStepFromCancelFlow();
        }}
        onSwitchToStandard={(targetPlanAlias) => {
          setShowCancelDialog(false);
          // Target is dynamic: Engage's retention offer is Pro Plus, not Standard.
          // Forward whatever the retention card resolved to instead of hardcoding.
          w.switchPlanFromCancelFlow(targetPlanAlias);
        }}
        onSwitchToFree={() => {
          // Same path as the plan-step "Select Free" button — closes the cancel
          // dialog and opens DowngradeSurveyDialog (then DowngradeComparisonDialog).
          setShowCancelDialog(false);
          w.handleSelectFree();
        }}
        onKeepSubscription={() => {
          // Tear down both popups (cancel dialog + wizard) and return the user
          // to the Subscription page they came from.
          setShowCancelDialog(false);
          w.handleOpenChange(false);
          navigate('/app/s/subscription-plan');
        }}
      />
    </Dialog>
  );
}
